import { useQuery } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import * as Sentry from '@sentry/react';
import { toSentryError } from '@pps/observability';
import i18n from '@/i18n';

// Tier pro removido do catálogo (20260703150000_remove_pro_tier.sql).
export type PlanTier = 'free' | 'enterprise';

/**
 * Row shape of `platform_fee_settings_v2` (rows-per-tier schema).
 */
export interface PlatformFee {
  plan_tier: PlanTier;
  pct: number;
  updated_at: string;
}

export const TIERS: readonly PlanTier[] = ['free', 'enterprise'] as const;

/**
 * Checks whether current authenticated user has the PPS global `admin` role.
 * Uses the `has_role` SECURITY DEFINER RPC.
 */
export function useIsPlatformAdmin() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['is-platform-admin', user?.id],
    queryFn: async (): Promise<boolean> => {
      if (!user?.id) return false;
      const { data, error } = await supabase.rpc('has_role', {
        _role: 'admin',
        _user_id: user.id,
      });
      if (error) {
        // `error` aqui é o objeto plano do postgrest-js, não um Error — sem
        // `toSentryError` a issue chega ao Sentry como "Object captured as
        // exception with keys: code, details, hint, message" (PLAYPROSPORT-16).
        Sentry.captureException(toSentryError(error), {
          tags: { feature: 'platform-fees', action: 'check-admin' },
        });
        return false;
      }
      return Boolean(data);
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetches all rows from `platform_fee_settings_v2` (one row per plan tier).
 */
export function usePlatformFees() {
  return useQuery({
    queryKey: ['platform-fee-settings', 'v2'],
    queryFn: async (): Promise<PlatformFee[]> => {
      const { data, error } = await supabase
        .from('platform_fee_settings_v2')
        .select('plan_tier, pct, updated_at')
        .order('plan_tier');
      if (error) throw error;
      return (data as PlatformFee[] | null) ?? [];
    },
  });
}

export interface UpdatePlatformFeeInput {
  tier: PlanTier;
  pct: number;
}

export function useUpdatePlatformFee() {
  return useDomainMutation({
    mutationFn: async (input: UpdatePlatformFeeInput): Promise<PlatformFee> => {
      const { tier, pct } = input;

      // Client-side validation (DB CHECK is the source of truth).
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        throw new Error('pct must be between 0 and 100');
      }

      const { data, error } = await supabase
        .from('platform_fee_settings_v2')
        .update({ pct })
        .eq('plan_tier', tier)
        .select('plan_tier, pct, updated_at')
        .single();
      if (error) throw error;
      return data as PlatformFee;
    },
    invalidates: [['platform-fee-settings', 'v2']],
    toast: { success: i18n.t('toasts.platformFeeUpdated'), error: i18n.t('toasts.error') },
    tags: { feature: 'platform-fees', action: 'update' },
    onError: (error, variables) => {
      // Preserve `extra.tier` context in addition to wrapper's default capture
      Sentry.captureException(toSentryError(error), {
        tags: { feature: 'platform-fees', action: 'update' },
        extra: { tier: variables.tier },
      });
    },
  });
}
